import React, { useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal as RNModal,
  Platform,
  Dimensions,
  ScrollView,
} from 'react-native';
import { overlayBackdropStyle, overlayCardLook, useEscape } from './OverlayPanel';
import { useIsMobileBrowser } from '../hooks/useIsMobileBrowser';
import { useRightPaneWidth } from '../hooks/useRightPaneWidth';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors, spacing, typography } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useAppStore } from '../store/appStore';
import { useThemedStyles } from '../hooks/useThemedStyles';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

export interface ModalProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  size?: 'small' | 'medium' | 'large' | 'full';
  type?: 'default' | 'confirmation' | 'form';
  footer?: React.ReactNode;
  scrollable?: boolean;
  preventBackdropScroll?: boolean;
  anchor?: { x: number; y: number };
  constrainToContentArea?: boolean; // On web, constrain modal to middle content area (not covering side panes)
  customHeader?: React.ReactNode; // Custom header content to replace default title
  headerBackgroundColor?: string; // Custom header background color
}

export const Modal: React.FC<ModalProps> = ({
  visible,
  onClose,
  title,
  children,
  showCloseButton = true,
  closeOnBackdrop = true,
  size = 'medium',
  type = 'default',
  footer,
  scrollable = false,
  preventBackdropScroll = true,
  anchor,
  constrainToContentArea = false,
  customHeader,
  headerBackgroundColor,
}) => {
  const styles = useThemedStyles(getStyles);
  // Desktop web: placed like global search (near the top), with an Esc key that closes it.
  const isMobileBrowser = useIsMobileBrowser();
  const desktop = Platform.OS === 'web' && !isMobileBrowser;
  useEscape(visible && desktop && closeOnBackdrop, onClose);
  const { isRTL } = useLocale();
  const typo = useTypography();
  const rtl = useRTLStyles();
  const { leftPaneCollapsed, leftPaneWidth, rightPaneCollapsed } = useAppStore();
  const rightPaneWidth = useRightPaneWidth();
  // Only meaningfully non-zero for size:'full' — every other size stays
  // centered with generous vertical padding (see styles.overlay), never
  // actually touching the screen edges, so nothing there needs insets.
  // size:'full' spans the whole device height/width edge-to-edge instead,
  // the same class of "content under the notch / against the home
  // indicator" bug already fixed for VisitExecuteScreen's fullscreen Modal
  // and ContactsListScreen's editContactFullScreen overlay.
  const insets = useSafeAreaInsets();

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (visible && preventBackdropScroll && Platform.OS === 'web') {
      // Prevent body scroll on web
      const originalStyle = window.document.body.style.overflow;
      window.document.body.style.overflow = 'hidden';
      
      return () => {
        window.document.body.style.overflow = originalStyle;
      };
    }
  }, [visible, preventBackdropScroll]);

  const handleBackdropPress = () => {
    if (closeOnBackdrop) {
      onClose();
    }
  };

  const getModalSize = () => {
    const isMobile = screenWidth < 768;
    const horizontalPadding = isMobile ? spacing.lg : spacing.xl * 2;
    
    switch (size) {
      case 'small': {
        const width = isMobile
          ? Math.min(screenWidth - horizontalPadding * 2, 420)
          : 360;
        return {
          width,
          maxWidth: width,
          minWidth: isMobile ? Math.min(width, 280) : 320,
        };
      }
      case 'medium':
        return { 
          width: isMobile ? '95%' : '85%', 
          maxWidth: isMobile ? screenWidth - (horizontalPadding * 2) : 500,
          minWidth: isMobile ? 300 : 350
        };
      case 'large':
        return { 
          width: isMobile ? '98%' : '90%', 
          maxWidth: isMobile ? screenWidth - (horizontalPadding * 2) : 700,
          minWidth: isMobile ? 320 : 400
        };
      case 'full':
        return { width: '100%', height: '100%' };
      default:
        return { 
          width: isMobile ? '95%' : '85%', 
          maxWidth: isMobile ? screenWidth - (horizontalPadding * 2) : 500,
          minWidth: isMobile ? 300 : 350
        };
    }
  };

  const getModalStyles = () => {
    const baseStyles = {
      backgroundColor: colors.background.surfaceRaised,
      borderRadius: spacing.borderRadius.lg,
      ...getModalSize(),
    };

    if (size === 'full') {
      return {
        ...baseStyles,
        height: '100%',
        borderRadius: 0,
      };
    }

    return baseStyles;
  };

  const renderContent = () => {
    if (scrollable) {
      return (
        <ScrollView 
          style={styles.scrollContent}
          contentContainerStyle={styles.scrollContentContainer}
          showsVerticalScrollIndicator={true}
          bounces={false}
          nestedScrollEnabled={true}
        >
          {children}
        </ScrollView>
      );
    }
    return <View style={styles.content}>{children}</View>;
  };

  const modalStyles = getModalStyles();

  const resolvedWidth =
    typeof modalStyles.width === 'number'
      ? modalStyles.width
      : typeof modalStyles.maxWidth === 'number'
      ? modalStyles.maxWidth
      : Math.min(screenWidth - 32, size === 'small' ? 420 : size === 'medium' ? 480 : 560);

  const resolvedMaxHeight =
    typeof modalStyles.maxHeight === 'number'
      ? modalStyles.maxHeight
      : screenHeight - 32;

  const containerStyles: any = {
    ...modalStyles,
    width: resolvedWidth,
    maxWidth: resolvedWidth,
    maxHeight: resolvedMaxHeight,
  };

  if (size === 'full') {
    containerStyles.width = '100%';
    containerStyles.maxWidth = '100%';
    containerStyles.maxHeight = '100%';
    // Confirmed real bug: size:'full' spans the entire device screen with
    // borderRadius:0 (a true full-screen takeover, unlike every other size
    // which stays centered/inset within generous overlay padding) — its
    // header/footer need the actual safe-area padding or they render under
    // the notch/Dynamic Island and hard against the home-indicator area.
    containerStyles.paddingTop = insets.top;
    containerStyles.paddingBottom = insets.bottom;
  }

  const estimatedHeight =
    size === 'small'
      ? Math.min(resolvedMaxHeight, 360)
      : size === 'medium'
      ? Math.min(resolvedMaxHeight, 440)
      : size === 'large'
      ? Math.min(resolvedMaxHeight, 520)
      : resolvedMaxHeight;

  let anchorStyle: { position: 'absolute'; left: number; top: number } | undefined;
  let anchorPlacement: 'above' | 'below' = 'below';
  let arrowOffset = resolvedWidth / 2;

  if (anchor) {
    anchorPlacement = anchor.y > screenHeight / 2 ? 'above' : 'below';

    let top =
      anchorPlacement === 'below'
        ? anchor.y + 12
        : anchor.y - estimatedHeight - 12;

    if (anchorPlacement === 'below' && top + estimatedHeight + 16 > screenHeight) {
      anchorPlacement = 'above';
      top = anchor.y - estimatedHeight - 12;
    }

    if (anchorPlacement === 'above' && top < 16) {
      anchorPlacement = 'below';
      top = anchor.y + 12;
    }

    if (anchorPlacement === 'below') {
      top = Math.min(top, screenHeight - estimatedHeight - 16);
      if (top < 16) {
        top = 16;
      }
    } else {
      top = Math.max(top, 16);
    }

    let left = anchor.x - resolvedWidth / 2;
    left = Math.max(16, Math.min(left, screenWidth - resolvedWidth - 16));

    anchorStyle = {
      position: 'absolute',
      left,
      top,
    };

    arrowOffset = anchor.x - left;
    arrowOffset = Math.max(24, Math.min(arrowOffset, resolvedWidth - 24));
  }

  // Calculate content area constraints for web — reads the live, possibly
  // collapsed/resized pane widths from the store rather than assuming both
  // panes are always present at a fixed 355px (that assumption predates
  // the panes becoming collapsible/resizable and was already a heuristic).
  const getContentAreaConstraints = () => {
    if (!constrainToContentArea || Platform.OS !== 'web') {
      return {};
    }

    return {
      left: leftPaneCollapsed ? 0 : leftPaneWidth,
      right: rightPaneCollapsed ? 0 : rightPaneWidth,
    };
  };

  const contentAreaConstraints = getContentAreaConstraints();

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={[
        styles.overlay, 
        anchorStyle ? styles.overlayAnchored : null,
        desktop && !anchorStyle && !constrainToContentArea && size !== 'full' ? styles.overlayTop : null,
        Platform.OS === 'web' && constrainToContentArea && styles.overlayConstrained,
        contentAreaConstraints,
      ]}>
        <TouchableOpacity
          style={styles.backdrop}
          activeOpacity={1}
          onPress={handleBackdropPress}
        />
        <View
          style={[
            styles.modalContainer,
            containerStyles,
            anchorStyle,
          ]}
        >
          {anchor && (
            <View
              style={[
                styles.anchorIndicator,
                anchorPlacement === 'above'
                  ? styles.anchorIndicatorBelow
                  : styles.anchorIndicatorAbove,
                { left: arrowOffset - 10 },
              ]}
            />
          )}
          {/* Header */}
          <View style={[
            styles.header,
            { flexDirection: rtl.rowDirection },
            headerBackgroundColor && { backgroundColor: headerBackgroundColor }
          ]}>
            {customHeader ? (
              <>
                {customHeader}
                {showCloseButton && (
                  <TouchableOpacity
                    style={styles.closeButton}
                    onPress={onClose}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons name="close" size={20} color={colors.text.primary as string} />
                  </TouchableOpacity>
                )}
              </>
            ) : (
              <>
                <Text style={[styles.title, { fontFamily: typo.fontFamily.semibold, textAlign: rtl.textStart }]}>{title}</Text>
                {showCloseButton && (
                  <TouchableOpacity
                    style={[styles.closeButton, rtl.marginStart(spacing.sm)]}
                    onPress={onClose}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    accessibilityLabel="Close"
                  >
                    {desktop ? <Text style={styles.kbd}>Esc</Text> : <Ionicons name="close" size={24} color={colors.text.secondary} />}
                  </TouchableOpacity>
                )}
              </>
            )}
          </View>

          {/* Content */}
          <View style={styles.contentArea}>
            {renderContent()}
          </View>

          {/* Footer */}
          {footer && (
            <View style={styles.footer}>
              {footer}
            </View>
          )}
        </View>
      </View>
    </RNModal>
  );
};

// Confirmation Modal Component
export interface ConfirmationModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'info' | 'error' | 'success';
  loading?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  visible,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  loading = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const getIconName = () => {
    switch (type) {
      case 'warning':
        return 'warning';
      case 'info':
        return 'information-circle';
      case 'error':
        return 'close-circle';
      case 'success':
        return 'checkmark-circle';
      default:
        return 'warning';
    }
  };

  const getIconColor = () => {
    switch (type) {
      case 'warning':
        return colors.warning;
      case 'info':
        return colors.info;
      case 'error':
        return colors.error;
      case 'success':
        return colors.success;
      default:
        return colors.warning;
    }
  };

  const getConfirmButtonStyle = () => {
    switch (type) {
      case 'warning':
        return styles.warningButton;
      case 'info':
        return styles.infoButton;
      case 'error':
        return styles.errorButton;
      case 'success':
        return styles.successButton;
      default:
        return styles.warningButton;
    }
  };

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      title={title}
      size="small"
      type="confirmation"
      closeOnBackdrop={!loading}
    >
      <View style={styles.confirmationContent}>
        <View style={styles.confirmationIcon}>
          <Ionicons name={getIconName()} size={48} color={getIconColor()} />
        </View>
        <Text style={styles.confirmationMessage}>{message}</Text>
      </View>
      
      <View style={styles.confirmationFooter}>
        <TouchableOpacity
          style={styles.cancelButton}
          onPress={onClose}
          disabled={loading}
        >
          <Text style={styles.cancelButtonText}>{cancelText}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[getConfirmButtonStyle(), loading && styles.buttonDisabled]}
          onPress={onConfirm}
          disabled={loading}
        >
          <Text style={styles.confirmButtonText}>
            {loading ? 'Loading...' : confirmText}
          </Text>
        </TouchableOpacity>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    ...overlayBackdropStyle(),
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
    minHeight: '100%',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    }),
  },
  overlayTop: {
    justifyContent: 'flex-start',
    paddingTop: '9vh' as any,
  },
  overlayAnchored: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.lg,
  },
  overlayConstrained: {
    ...(Platform.OS === 'web' && {
      position: 'absolute' as any,
      top: 180, // Header height (matches webContentArea paddingTop)
      bottom: 0,
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.lg,
    }),
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Prevent scrolling on backdrop
    zIndex: 1,
  },
  modalContainer: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.lg,
    maxHeight: '90%',
    minHeight: Platform.OS === 'ios' ? 520 : 300, // Increased minHeight on iOS for calendar modal
    maxWidth: '95%',
    width: 'auto',
    shadowColor: colors.literal.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
    marginHorizontal: spacing.sm,
    marginVertical: spacing.sm,
    zIndex: 2,
    overflow: 'hidden',
    flexDirection: 'column',
    ...(Platform.OS === 'web' && {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(90vh - 40px)',
      height: 'auto',
    }),
    ...overlayCardLook(),
  },
  contentArea: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      flexShrink: 1,
      minHeight: 150,
    }),
  },
  anchorIndicator: {
    position: 'absolute',
    width: 0,
    height: 0,
    borderLeftWidth: 10,
    borderRightWidth: 10,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },
  anchorIndicatorAbove: {
    top: -10,
    borderBottomWidth: 10,
    borderBottomColor: colors.background.surfaceRaised,
  },
  anchorIndicatorBelow: {
    bottom: -10,
    borderTopWidth: 10,
    borderTopColor: colors.background.surfaceRaised,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    flexShrink: 0,
    minHeight: 48,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
    flex: 1,
  },
  closeButton: {
    padding: spacing.sm,
  },
  kbd: {
    fontSize: 11,
    color: colors.text.secondary,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  content: {
    padding: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.md,
    minHeight: Platform.OS === 'ios' ? 480 : 100, // Increased minHeight on iOS for calendar content
  },
  scrollContent: {
    flex: 1,
    maxHeight: Platform.OS === 'web' ? 'calc(90vh - 250px)' : 400,
    minHeight: 150,
  },
  scrollContentContainer: {
    padding: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.md,
  },
  footer: {
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    flexShrink: 0,
    backgroundColor: colors.background.surfaceRaised,
    minHeight: 70,
    ...(Platform.OS === 'web' && {
      position: 'relative',
      zIndex: 10,
      flexShrink: 0,
      order: 999,
    }),
  },
  // Confirmation modal styles
  confirmationContent: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
  },
  confirmationIcon: {
    marginBottom: spacing.lg,
  },
  confirmationMessage: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 20,
  },
  confirmationFooter: {
    flexDirection: 'column-reverse',
    gap: spacing.xs,
    padding: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  cancelButton: {
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cancelButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
  },
  warningButton: {
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: colors.warning,
  },
  infoButton: {
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: colors.info,
  },
  errorButton: {
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: colors.error,
  },
  successButton: {
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: colors.success,
  },
  confirmButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.white,
    fontWeight: '600',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
});
